import Admin from "../Admin";
import React from "react";
import CardAdminTermGeneral from "../../../Component/Card/Admin/Term/CardAdminTermGeneral";
import PageHeader from "../../../Component/PageHeader";
import MUIButton from "@mui/material/Button";
import MUIDeleteIcon from "@mui/icons-material/Delete";
import MUISaveButton from "@mui/icons-material/Save";
import Menu from "../../../Component/Menu";
import DialogConfirm from "../../../Component/Dialog/DialogConfirm";
import MUIMenuItem from "@mui/material/MenuItem";
import MUIListItemIcon from "@mui/material/ListItemIcon";
import TermService from "../../../Seating/Security/TermService/termService";
import Validator from "../../../Utility/Validator";
import Term from "../../../Utility/Crud/Term";
import { enqueueSnackbar as NotistackEnqueueSnackbar } from "notistack";

export default class AdminTermCreateEdit extends Admin {
  constructor(props) {
    super(props);

    this.state.term = null;
    this.state.isProcessing = false;
    this.state.isUpdate = !!props.match.params.id;
    this.state.confirmDialogOpen = false;
    this.state.validationIssues = {};
  }

  /**
   * Set the term in the state.
   */
  afterComponentDidMount() {
    const termId = this.props.match.params.id
      ? +this.props.match.params.id
      : null;
    if (termId !== null) {
      this.setState({
        term: this.context.termsIndexed[termId],
      });
    }
  }

  /**
   * Creates the term with the current properties in state.term.
   */
  async createTerm() {
    this.setState({
      isProcessing: true,
    });

    return TermService.createTerm({
      ...this.state.term,
      type: this.getType(),
    })
      .then((term) => {
        this.context.updateIndexed("termsIndexed", term);
        this.props.history.goBack();
        NotistackEnqueueSnackbar("Term created successfully!", {
          variant: "success",
        });
      })
      .catch((err) => {
        const termTypeName = Term.getTermTypes()[this.getType()].name;
        // JZ. We need to test and make sure that Sentry still captures
        // errors when we catch them in the application.
        console.log(err);
        NotistackEnqueueSnackbar(
          `Oh no! There was an error creating a new ${termTypeName}.`,
          { variant: "error" },
        );
        this.setState({
          isProcessing: false,
        });
      });
  }

  /**
   * Updates the term with the current properties in state.term.
   */
  async updateTerm() {
    this.setState({
      isProcessing: true,
    });

    return TermService.updateTerm(this.state.term)
      .then((term) => {
        this.context.updateIndexed("termsIndexed", term);
        this.props.history.goBack();
        NotistackEnqueueSnackbar("Team updated successfully!", {
          variant: "success",
        });
      })
      .catch((err) => {
        const termTypeName = Term.getTermTypes()[this.getType()].name;
        console.log(err);
        NotistackEnqueueSnackbar(
          `Oh no! There was an error updating the ${termTypeName}.`,
          { variant: "error" },
        );
        this.setState({
          isProcessing: false,
        });
      });
  }

  async deleteTerm() {
    this.setState({
      isProcessing: true,
    });

    return TermService.deleteTerm(this.state.term.id)
      .then(() => {
        this.context.deleteIndexed("termsIndexed", this.state.term.id);
        this.props.history.goBack();
        NotistackEnqueueSnackbar("Team deleted successfully!", {
          variant: "success",
        });
      })
      .catch((err) => {
        const termTypeName = Term.getTermTypes()[this.getType()].name;
        console.log(err);
        NotistackEnqueueSnackbar(
          `Oh no! There was an error deleting the ${termTypeName}.`,
          { variant: "error" },
        );
        this.setState({
          confirmDialogOpen: false,
        });
      });
  }

  /**
   * Render the content.
   */
  renderContent() {
    const handleChange = (term) => {
      this.setState({
        term: {
          ...this.state.term,
          ...term,
        },
      });
    };

    const termTypeName = Term.getTermTypes()[this.getType()].name;
    return (
      <>
        <CardAdminTermGeneral
          term={this.state.term}
          onChange={handleChange.bind(this)}
          validationIssues={this.state.validationIssues}
        />
        <DialogConfirm
          open={this.state.confirmDialogOpen}
          onClose={() => this.setState({ confirmDialogOpen: false })}
          onConfirm={() => this.deleteTerm()}
          text={`Are you sure you want to delete this ${termTypeName}? This cannot be undone.`}
          header={`Delete ${termTypeName}`}
          loading={this.state.isProcessing}
        />
      </>
    );
  }

  /**
   * Renders the header.
   */
  renderHeader() {
    let title;
    let subtitle;
    const buttonText = this.state.isUpdate ? "Save" : "Create";

    const termTypeName = Term.getTermTypes()[this.getType()].name;

    if (!this.state.isUpdate) {
      title = `Create ${termTypeName}`;
      subtitle = null;
    } else {
      title = `Edit ${termTypeName}`;
      subtitle =
        this.state.term &&
        Term.getDisplayName(this.state.term) + " • " + this.state.term.id;
    }

    const handleClickBack = () => {
      this.props.history.goBack();
    };

    const handleClickSave = () => {
      const validationIssues = Validator.validate(this.state.term, {
        name: {
          required: true,
        },
      });

      this.setState({
        validationIssues: validationIssues,
      });

      if (Object.keys(validationIssues).length === 0) {
        if (this.state.isUpdate) {
          this.updateTerm();
        } else {
          this.createTerm();
        }
      } else {
        NotistackEnqueueSnackbar("Please correct the displayed issues.", {
          variant: "error",
        });
      }
    };

    const handleDelete = () => {
      this.setState({
        confirmDialogOpen: true,
      });
    };

    const buttons = [
      <MUIButton onClick={handleClickBack}>Back</MUIButton>,
      <MUIButton
        loading={this.state.isProcessing}
        loadingPosition="start"
        onClick={handleClickSave}
        variant="contained"
        startIcon={<MUISaveButton />}
      >
        {buttonText}
      </MUIButton>,
    ];

    if (this.state.isUpdate) {
      const menuItems = [
        <MUIMenuItem onClick={handleDelete}>
          <MUIListItemIcon>
            <MUIDeleteIcon fontSize="small" />
          </MUIListItemIcon>
          Delete
        </MUIMenuItem>,
      ];
      buttons.push(<Menu menuItems={menuItems} />);
    }

    return <PageHeader title={title} subtitle={subtitle} buttons={buttons} />;
  }
}
